import { DESKTOP_MENU_SURFACE } from '@crowdex/ui/components/desktop';
import { cn } from '@crowdex/ui/lib/utils';
import {
  type ComponentProps,
  type CSSProperties,
  forwardRef,
  type ReactNode,
  type RefObject,
} from 'react';

/** The surface of the composer's picker (+, / and @), above the composer or below it. */
export function ComposerMenuPanel({
  bodyClassName,
  children,
  className,
  footer,
  header,
  label,
  menuRef,
  placement = 'above',
  role = 'listbox',
  sheet = false,
  style,
}: {
  bodyClassName?: string;
  children: ReactNode;
  className?: string;
  /** A line under the list: its keys. */
  footer?: ReactNode;
  header?: ReactNode;
  /** Its accessible name ("Skills", "Mention", "Add context"). */
  label?: string;
  menuRef?: RefObject<HTMLDivElement | null>;
  placement?: 'above' | 'below';
  role?: 'listbox' | 'dialog';
  /** On a touch screen, a sheet from the bottom (not an autocomplete, which follows typing). */
  sheet?: boolean;
  style?: CSSProperties;
}) {
  return (
    // biome-ignore lint/a11y/useAriaPropsSupportedByRole: its role (a listbox or a dialog) is a prop; both take a name.
    <div
      aria-label={label}
      className={cn(
        DESKTOP_MENU_SURFACE,
        'absolute right-0 left-0 z-30 overflow-hidden p-0',
        placement === 'below' ? 'top-full' : 'bottom-full',
        placement === 'below' ? (header ? 'mt-1' : 'mt-2') : header ? 'mb-1' : 'mb-2',
        className,
      )}
      data-composer-menu
      data-placement={placement}
      data-sheet={sheet || undefined}
      ref={menuRef}
      role={role}
      style={style}
    >
      {header}
      <div className={cn('max-h-72 overflow-y-auto p-1', bodyClassName)}>{children}</div>
      {footer !== undefined && (
        <p className="type-caption px-3 pt-0.5 pb-2 text-muted-foreground pointer-coarse:hidden">
          {footer}
        </p>
      )}
    </div>
  );
}

/** A row of the picker: 32 px (a finger's 44 on a phone), its glyph, its label, what follows. */
export const PickerRow = forwardRef<
  HTMLButtonElement,
  Omit<ComponentProps<'button'>, 'children'> & {
    active: boolean;
    icon: ReactNode;
    label: ReactNode;
    description?: string | undefined;
    trailing?: ReactNode;
    touch?: boolean;
    mono?: boolean;
  }
>(function PickerRow(
  { active, icon, label, description, trailing, touch = false, mono = false, className, ...props },
  ref,
) {
  return (
    <button
      className={cn(
        'type-ui flex w-full items-center gap-2.5 rounded-md px-2 text-left text-foreground transition-colors disabled:opacity-50',
        touch ? 'min-h-11' : 'h-8',
        active ? 'bg-accent' : 'hover:bg-accent/60',
        className,
      )}
      data-active={active || undefined}
      onMouseDown={(event) => event.preventDefault()}
      ref={ref}
      type="button"
      {...props}
    >
      {icon}
      <span className="max-w-[55%] shrink-0 truncate">{label}</span>
      {description !== undefined && description.length > 0 && (
        <span
          className={cn(
            'type-meta min-w-0 flex-1 truncate text-muted-foreground',
            mono && 'font-mono',
          )}
        >
          {description}
        </span>
      )}
      <span className="ml-auto flex shrink-0 items-center gap-1.5">{trailing}</span>
    </button>
  );
});
